import { Tag, Tooltip } from 'antd';
import { Link } from 'react-router-dom';

// Stage 5N-2C: shared cell renderers used by all four
// account-list pages (Pending / Approved / Rejected / Suspended).
// Centralising them here keeps column widths + ellipsis behaviour
// consistent across pages.

export function paiseToINR(paise) {
  if (paise == null) return '—';
  const n = Number(paise);
  if (!Number.isFinite(n)) return '—';
  return `₹${(n / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
}

export function MoneyText({ paise, className = '' }) {
  return (
    <span className={`text-xs font-medium text-ink-800 ${className}`}>
      {paiseToINR(paise)}
    </span>
  );
}

// Account-status pill matching the four backend values + lifecycle
// `master`/`active` shorthand for users.
export function AccountStatusTag({ status }) {
  const COLOR = {
    pending_approval: 'orange',
    active:           'green',
    suspended:        'red',
    rejected:         'default'
  };
  return <Tag color={COLOR[status] || 'default'}>{status || '—'}</Tag>;
}

// Company column: name on top + slug below in a smaller mono font.
// Wraps the name in a Link to the detail page so the operator can
// click the name itself (matches the explicit "View" button in the
// Actions column).
export function CompanyCell({ row }) {
  if (!row) return null;
  const name = row.legalName || row.displayName || row.slug || '—';
  return (
    <div className="leading-tight min-w-0">
      <Link
        to={`/internal/accounts/${row.tenantId}`}
        className="text-sm font-semibold text-ink-900 hover:text-brand-600 truncate block"
        title={name}
      >
        {name}
      </Link>
      {row.slug ? (
        <code
          className="text-[11px] text-ink-400 truncate block"
          title={row.slug}
        >
          {row.slug}
        </code>
      ) : null}
    </div>
  );
}

export function ContactCell({ row }) {
  if (!row) return null;
  return (
    <div className="leading-tight text-xs min-w-0">
      <p className="m-0 font-medium text-ink-800 truncate" title={row.contactName || ''}>
        {row.contactName || '—'}
      </p>
      <p className="m-0 text-ink-500 truncate" title={row.contactEmail || ''}>
        {row.contactEmail || '—'}
      </p>
      <p className="m-0 text-ink-500 truncate" title={row.contactMobile || ''}>
        {row.contactMobile || '—'}
      </p>
    </div>
  );
}

export function PlanCell({ row }) {
  if (!row) return null;
  const plan = row.planName || row.billingCycle || '—';
  return (
    <div className="leading-tight">
      <span className="capitalize text-sm font-medium text-ink-800">{plan}</span>
      {row.billingCycle ? (
        <p className="text-[11px] text-ink-500 m-0 capitalize">
          {row.billingCycle}
          {row.isTrial ? (
            <Tag color="gold" style={{ marginLeft: 4, fontSize: 10 }} bordered={false}>
              trial
            </Tag>
          ) : null}
        </p>
      ) : row.isTrial ? (
        <Tag color="gold" style={{ marginTop: 2, fontSize: 10 }} bordered={false}>
          trial
        </Tag>
      ) : null}
    </div>
  );
}

// Truncated Razorpay payment id with order-id tooltip.
export function PaymentCell({ row }) {
  if (!row || !row.razorpayPaymentId) {
    return <span className="text-ink-400">—</span>;
  }
  return (
    <Tooltip title={`Order: ${row.razorpayOrderId || '—'}`} mouseEnterDelay={0.3}>
      <code className="text-[11px] text-ink-700 truncate inline-block max-w-[140px] align-middle">
        {row.razorpayPaymentId}
      </code>
    </Tooltip>
  );
}
